import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, 
  Database, 
  FileText, 
  Loader2,
  Upload,
  X,
  CheckCircle,
  Cloud,
  Server,
  Link2,
  Monitor,
  Trash2,
  Eye,
  ChevronDown,
  Settings
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { apiService } from '../services/apiService';
import ProcessLoadingScreen from '../components/ProcessLoadingScreen';

interface DatabaseConnection {
  type: 'mysql' | 'postgresql' | 'oracle' | 'sqlserver';
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  schema?: string;
}

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const { showSuccess, showError } = useNotification();
  const { token, ready } = useAuth();
  
  // Loading states
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  
  // Upload modal states
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const [runName, setRunName] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Database connection modal states
  const [showDbModal, setShowDbModal] = useState(false);
  const [dbType, setDbType] = useState<'mysql' | 'postgresql' | 'oracle' | 'sqlserver'>('mysql');
  const [dbConnection, setDbConnection] = useState<DatabaseConnection>({
    type: 'mysql',
    host: 'localhost',
    port: '3306',
    database: '',
    username: '',
    password: '',
    schema: ''
  });
  const [dbRunName, setDbRunName] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [showDbConnections, setShowDbConnections] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);

  useEffect(() => {
    if (!ready) return;

    // If logged out, clear runs immediately
    if (!token) {
      dispatch({ type: 'SET_RUNS', payload: [] });
      return;
    }

    // When token changes (user logs in or switches), reload runs for that user
    loadRuns();
  }, [token, ready]);

  const loadRuns = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const runs = await apiService.getRuns();
      // List endpoint already returns has_schema, input_csv_files, synthetic_csv_files
      dispatch({ type: 'SET_RUNS', payload: Array.isArray(runs) ? runs : [] });
    } catch (error) {
      console.error('Failed to load runs:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to load runs');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleNewRunWithCSV = async () => {
    // Show modal first to get run name
    setShowUploadModal(true);
  };

  const handleCreateRunAndUpload = async () => {
    if (selectedFiles.length === 0) {
      showError('Error', 'Please select CSV files to upload');
      return;
    }

    setIsUploading(true);
    setShowLoadingScreen(true);
    setLoadingStep(0);

    const steps = [
      'Creating new run',
      'Uploading files',
      'Saving to inputs folder',
      'Running data analysis',
      'Detecting table relationships',
      'Generating schema',
      'Finalizing run setup'
    ];

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % steps.length);
    }, 1500);

    try {
      // Step 1: Create run
      setLoadingStep(0);
      const result = await apiService.createNewRun(runName || undefined);
      
      if (!result || !result.success) {
        clearInterval(stepInterval);
        setShowLoadingScreen(false);
        setIsUploading(false);
        showError('Error', result?.error || 'Failed to create new run');
        return;
      }

      const newRunId = result.run_id;
      setCurrentRunId(newRunId);
      await loadRuns();

      // Step 2: Upload files
      setLoadingStep(1);
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const uploadResult = await apiService.uploadFiles(newRunId, formData);

      if (!uploadResult || !uploadResult.success) {
        clearInterval(stepInterval);
        setShowLoadingScreen(false);
        setIsUploading(false);
        showError('Error', uploadResult?.error || 'Failed to upload files');
        return;
      }

      showSuccess('Success', `Uploaded ${uploadResult.uploaded_files?.length || 0} file(s)`);
      
      // Step 3: Analyze
      setLoadingStep(3);
      const analyzerResult = await apiService.analyzeRun(newRunId);
      
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      setShowUploadModal(false);
      setSelectedFiles([]);
      setCurrentRunId(null);
      setRunName('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (analyzerResult && analyzerResult.success) {
        showSuccess('Success', `Analysis complete! Analyzed ${analyzerResult.num_tables || 0} tables.`);
        await loadRuns();
        navigate(`/run/${newRunId}/select`);
      } else {
        showError('Error', analyzerResult?.error || 'Analysis failed');
      }
    } catch (error) {
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      setIsUploading(false);
      console.error('Failed to create run and upload:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to create run and upload');
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const csvFiles = files.filter(f => f.name.toLowerCase().endsWith('.csv'));
    setSelectedFiles(prev => [...prev, ...csvFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    const csvFiles = files.filter(f => f.name.toLowerCase().endsWith('.csv'));
    setSelectedFiles(prev => [...prev, ...csvFiles]);
  };

  const handleUploadFiles = async () => {
    if (!currentRunId || selectedFiles.length === 0) {
      showError('Error', 'Please select CSV files to upload');
      return;
    }

    setIsUploading(true);
    setShowLoadingScreen(true);
    setLoadingStep(0);

    const steps = [
      'Uploading files',
      'Saving to inputs folder',
      'Running data analysis',
      'Detecting table relationships',
      'Generating schema',
      'Finalizing run setup'
    ];

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % steps.length);
    }, 1500);

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const result = await apiService.uploadFiles(currentRunId, formData);

      if (result && result.success) {
        showSuccess('Success', `Uploaded ${result.uploaded_files?.length || 0} file(s)`);
        
        setLoadingStep(2);
        
        const analyzerResult = await apiService.analyzeRun(currentRunId);
        
        clearInterval(stepInterval);
        setShowLoadingScreen(false);
        setShowUploadModal(false);
        setSelectedFiles([]);
        const savedRunId = currentRunId;
        setCurrentRunId(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        if (analyzerResult && analyzerResult.success) {
          showSuccess('Success', `Analysis complete! Analyzed ${analyzerResult.num_tables || 0} tables.`);
          await loadRuns();
          if (savedRunId) {
            navigate(`/run/${savedRunId}/select`);
          }
        } else {
          showError('Error', analyzerResult?.error || 'Analysis failed');
        }
      } else {
        clearInterval(stepInterval);
        setShowLoadingScreen(false);
        showError('Error', result?.error || 'Failed to upload files');
      }
    } catch (error) {
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      console.error('Failed to upload files:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to upload files');
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setSelectedFiles([]);
    setCurrentRunId(null);
    setRunName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Database connection handlers
  const handleOpenDbModal = (type: 'mysql' | 'postgresql' | 'oracle' | 'sqlserver') => {
    const defaultPorts: Record<string, string> = {
      mysql: '3306',
      postgresql: '5432',
      oracle: '1521',
      sqlserver: '1433'
    };
    
    setDbType(type);
    setDbConnection({
      type,
      host: 'localhost',
      port: defaultPorts[type],
      database: '',
      username: '',
      password: '',
      schema: ''
    });
    setDbRunName('');
    setConnectionStatus('idle');
    setShowDbModal(true);
  };

  const handleTestConnection = async () => {
    setConnectionStatus('testing');
    
    try {
      // TODO: Call backend API to test connection
      const response = await apiService.testDatabaseConnection(dbConnection);
      
      if (response.success) {
        setConnectionStatus('success');
        showSuccess('Connection Successful', 'Database connection test passed!');
      } else {
        setConnectionStatus('error');
        showError('Connection Failed', response.error || 'Could not connect to database');
      }
    } catch (error) {
      setConnectionStatus('error');
      showError('Connection Failed', error instanceof Error ? error.message : 'Could not connect to database');
    }
  };

  const handleConnectDatabase = async () => {
    if (!dbConnection.host || !dbConnection.database || !dbConnection.username) {
      showError('Error', 'Please fill in all required fields');
      return;
    }

    setIsConnecting(true);
    setShowLoadingScreen(true);
    setLoadingStep(0);

    const steps = [
      'Connecting to database',
      'Fetching table list',
      'Analyzing table structures',
      'Detecting relationships',
      'Generating schema',
      'Creating run'
    ];

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % steps.length);
    }, 1500);

    try {
      // Call backend API to connect and import from database
      const connectionWithName = { ...dbConnection, name: dbRunName || undefined };
      const result = await apiService.importFromDatabase(connectionWithName);
      
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      
      if (result && result.success) {
        setShowDbModal(false);
        showSuccess('Success', `Connected to ${dbType} and imported ${result.num_tables || 0} tables`);
        await loadRuns();
        if (result.run_id) {
          navigate(`/run/${result.run_id}/select`);
        }
      } else {
        showError('Error', result?.error || 'Failed to import from database');
      }
    } catch (error) {
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      console.error('Failed to connect to database:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to connect to database');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleUseTemplate = async (templateKey: string, templateName: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const result = await apiService.createRunFromTemplate(templateKey, templateName);
      if (result && result.success && result.run_id) {
        showSuccess('Success', `${templateName} template loaded successfully.`);
        // Navigate first so the user opens the run with tables immediately
        navigate(`/run/${result.run_id}/select`);
        // Refresh sidebar runs in background (don't block navigation)
        loadRuns().catch(() => {});
      } else {
        showError('Error', result?.error || 'Failed to create run from template');
      }
    } catch (error) {
      showError('Error', error instanceof Error ? error.message : 'Failed to create run from template');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const getDbIcon = (type: string) => {
    switch (type) {
      case 'mysql':
        return <Database className="w-6 h-6 text-orange-600" />;
      case 'postgresql':
        return <Database className="w-6 h-6 text-blue-600" />;
      case 'oracle':
        return <Database className="w-6 h-6 text-red-600" />;
      case 'sqlserver':
        return <Server className="w-6 h-6 text-purple-600" />;
      default:
        return <Database className="w-6 h-6 text-gray-600" />;
    }
  };

  const getDbColor = (type: string) => {
    switch (type) {
      case 'mysql': return 'bg-orange-100';
      case 'postgresql': return 'bg-blue-100';
      case 'oracle': return 'bg-red-100';
      case 'sqlserver': return 'bg-purple-100';
      default: return 'bg-gray-100';
    }
  };

  return (
    <div className="h-full">
      {/* Main Content */}
      <div className="h-full overflow-y-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Create Test Scenarios</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Connect to a database or upload CSV files to start generating synthetic data
          </p>
        </div>

        {/* Data Sources Section */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setShowDbConnections(prev => !prev)}
            className="flex items-center justify-between w-full mb-4"
          >
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Link2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-left">
                Database Connections
              </h2>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transform transition-transform ${
                showDbConnections ? 'rotate-0' : '-rotate-90'
              }`}
            />
          </button>

          {showDbConnections && (
            <>
              {/* Pre-configured notice */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6 flex items-start space-x-3 border border-gray-200 dark:border-gray-700">
                <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Connect to your databases</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Choose a database type below to configure connection settings and import table structures.
                  </p>
                </div>
              </div>

              {/* Data Source Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* MySQL */}
                <div
                  onClick={() => handleOpenDbModal('mysql')}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-lg hover:border-orange-300 dark:hover:border-orange-500 transition-all cursor-pointer group"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Database className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MySQL</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">MySQL / MariaDB</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Default port: 3306</p>
                </div>

                {/* PostgreSQL */}
                <div
                  onClick={() => handleOpenDbModal('postgresql')}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 dark:hover:border-blue-500 transition-all cursor-pointer group"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Database className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">PostgreSQL</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">PostgreSQL database</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Default port: 5432</p>
                </div>

                {/* Oracle */}
                <div
                  onClick={() => handleOpenDbModal('oracle')}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-lg hover:border-red-300 dark:hover:border-red-500 transition-all cursor-pointer group"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Database className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Oracle</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Oracle Database</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Default port: 1521</p>
                </div>

                {/* SQL Server */}
                <div
                  onClick={() => handleOpenDbModal('sqlserver')}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-500 transition-all cursor-pointer group"
                >
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Server className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">SQL Server</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Microsoft SQL Server</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Default port: 1433</p>
                </div>

                {/* REST API - Coming Soon */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 opacity-60 cursor-not-allowed">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center">
                      <Cloud className="w-6 h-6 text-green-600 dark:text-green-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">REST API</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">Connect to REST endpoints</p>
                    </div>
                  </div>
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Coming Soon</p>
                </div>

                {/* MongoDB - Coming Soon */}
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 opacity-60 cursor-not-allowed">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
                      <Database className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100">MongoDB</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">NoSQL document database</p>
                    </div>
                  </div>
                  <p className="text-xs text-orange-600 dark:text-orange-400 font-medium">Coming Soon</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Upload Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <button
            type="button"
            onClick={() => setShowUploadSection(prev => !prev)}
            className="w-full flex items-center justify-between text-left mb-6"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                <Upload className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Upload CSV Files</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Drop your CSV files here or click to browse</p>
              </div>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform ${
                showUploadSection ? 'rotate-0' : '-rotate-90'
              }`}
            />
          </button>

          {showUploadSection && (
            <div
              onDragOver={handleDragOver}
              onDrop={(e) => {
                handleDrop(e);
                if (!currentRunId) {
                  handleNewRunWithCSV();
                }
              }}
              onClick={() => {
                if (!currentRunId) {
                  handleNewRunWithCSV();
                } else {
                  fileInputRef.current?.click();
                }
              }}
              className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-12 text-center hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all cursor-pointer bg-gray-50/50 dark:bg-gray-900/50"
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
              />
              <Cloud className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-2">
                <span className="text-blue-600 dark:text-blue-400 font-medium">Click to upload</span> or drag and drop
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">CSV files only • Multiple files supported</p>
            </div>
          )}
        </div>

        {/* Pre-configured Templates Section */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                Pre-configured templates for
              </h2>
            </div>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Choose a real-world banking scenario to start from a ready-made template.
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <button
              type="button"
              onClick={() => handleUseTemplate('account_opening', 'Account Opening')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Retail Banking
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Savings Account Opening
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Customer KYC, risk profiling, and multi-account onboarding flows.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleUseTemplate('account_opening_single_table', 'Account Opening (Single Table)')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Retail Banking
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Account Opening (Single Table)
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                One denormalized table with columns merged from the 6-table savings account opening model.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleUseTemplate('credit_card_application', 'Credit Card Application')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Cards
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Credit Card Application
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Applications, bureau checks, approvals, and card activation journeys.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleUseTemplate('mortgage_loan_origination', 'Mortgage Loan Origination')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Lending
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Mortgage Loan Origination
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                End-to-end flows for property details, underwriting, and disbursement.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleUseTemplate('personal_loan_application', 'Personal Loan Application')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Lending
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Personal Loan Application
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Salary, employment, and repayment capacity checks for unsecured loans.
              </p>
            </button>

            <button
              type="button"
              onClick={() => handleUseTemplate('digital_wallet_onboarding', 'Digital Wallet Onboarding')}
              className="text-left bg-gray-50 dark:bg-gray-800/60 border border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl p-5 hover:border-emerald-500 hover:bg-emerald-50/40 dark:hover:bg-emerald-900/20 transition-colors"
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 mb-1">
                Digital Channels
              </p>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
                Digital Wallet Onboarding
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Mobile app sign-up, device binding, and wallet funding journeys.
              </p>
            </button>
          </div>
        </div>
      </div>

      {/* File Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Upload CSV Files</h2>
                <button
                  onClick={handleCloseUploadModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              {/* Run Name Input */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Run Name <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={runName}
                  onChange={(e) => setRunName(e.target.value)}
                  placeholder="e.g., Customer Data Analysis"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave empty to use automatic timestamp-based name
                </p>
              </div>
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl p-8 text-center hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-all cursor-pointer bg-gray-50/50 dark:bg-gray-900/50"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="h-12 w-12 text-blue-600 dark:text-blue-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-300">
                  <span className="text-blue-600 dark:text-blue-400 font-medium">Click to upload</span> or drag and drop
                </p>
                <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">CSV files only</p>
              </div>

              {selectedFiles.length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Selected files ({selectedFiles.length}):</p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024).toFixed(1)} KB</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== index))}
                          className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-3">
              <button
                onClick={handleCloseUploadModal}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRunAndUpload}
                disabled={selectedFiles.length === 0 || isUploading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <Upload size={16} />
                    <span>Create Run, Upload & Analyze</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Database Connection Modal */}
      {showDbModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${getDbColor(dbType)} dark:opacity-80 rounded-lg flex items-center justify-center`}>
                    {getDbIcon(dbType)}
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 capitalize">{dbType} Connection</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Enter your database credentials</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowDbModal(false)}
                  className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Run Name Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Run Name <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={dbRunName}
                  onChange={(e) => setDbRunName(e.target.value)}
                  placeholder="e.g., Production DB Import"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Leave empty to use automatic timestamp-based name
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Host *</label>
                  <input
                    type="text"
                    value={dbConnection.host}
                    onChange={(e) => setDbConnection({ ...dbConnection, host: e.target.value })}
                    placeholder="localhost"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Port</label>
                  <input
                    type="text"
                    value={dbConnection.port}
                    onChange={(e) => setDbConnection({ ...dbConnection, port: e.target.value })}
                    placeholder={dbType === 'mysql' ? '3306' : '5432'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Database Name *</label>
                <input
                  type="text"
                  value={dbConnection.database}
                  onChange={(e) => setDbConnection({ ...dbConnection, database: e.target.value })}
                  placeholder="my_database"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              {(dbType === 'postgresql' || dbType === 'oracle') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schema</label>
                  <input
                    type="text"
                    value={dbConnection.schema}
                    onChange={(e) => setDbConnection({ ...dbConnection, schema: e.target.value })}
                    placeholder={dbType === 'postgresql' ? 'public' : ''}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Username *</label>
                <input
                  type="text"
                  value={dbConnection.username}
                  onChange={(e) => setDbConnection({ ...dbConnection, username: e.target.value })}
                  placeholder="root"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password *</label>
                <input
                  type="password"
                  value={dbConnection.password}
                  onChange={(e) => setDbConnection({ ...dbConnection, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                />
              </div>

              {/* Connection Status */}
              {connectionStatus !== 'idle' && (
                <div className={`p-3 rounded-lg flex items-center space-x-2 ${
                  connectionStatus === 'testing' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' :
                  connectionStatus === 'success' ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300' :
                  'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                }`}>
                  {connectionStatus === 'testing' && <Loader2 size={16} className="animate-spin" />}
                  {connectionStatus === 'success' && <CheckCircle size={16} />}
                  {connectionStatus === 'error' && <X size={16} />}
                  <span className="text-sm font-medium">
                    {connectionStatus === 'testing' && 'Testing connection...'}
                    {connectionStatus === 'success' && 'Connection successful!'}
                    {connectionStatus === 'error' && 'Connection failed'}
                  </span>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button
                onClick={handleTestConnection}
                disabled={isConnecting || connectionStatus === 'testing'}
                className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50"
              >
                Test Connection
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setShowDbModal(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  disabled={isConnecting}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnectDatabase}
                  disabled={isConnecting || !dbConnection.host || !dbConnection.database || !dbConnection.username}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <Database size={16} />
                      <span>Connect & Import</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Process Loading Screen */}
      <ProcessLoadingScreen
        isVisible={showLoadingScreen}
        title={showDbModal ? "Connecting to Database" : "Uploading Files"}
        description={showDbModal ? "Importing tables and analyzing schema..." : "Uploading CSV files and analyzing..."}
        steps={showDbModal ? [
          'Connecting to database',
          'Fetching table list',
          'Analyzing table structures',
          'Detecting relationships',
          'Generating schema',
          'Creating run'
        ] : [
          'Uploading files',
          'Saving to inputs folder',
          'Running data analysis',
          'Detecting table relationships',
          'Generating schema',
          'Finalizing run setup'
        ]}
        currentStep={loadingStep}
      />
    </div>
  );
};

export default UploadPage;

